import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";

import { requests } from "../../../common/RestAPI";
import { base64Encoding, getTimestampToDate } from "../../../common/util";
import { SESSIONSTORAGE, FONT } from "../../../value/staticValues";
import { PAGE_URL, PLATFORM_URL } from "../../../value/Url";
import { ALERTS, ERROR_CODE, POPUP_TYPE } from "../../../value/type";

import { ValidationError } from "../../../common/ExceptionError";

import { DetailProps } from "../../../interfaceTypes/ContentsProps";
import VmDetail from "./VmDetail";
import { useTranslation } from "react-i18next";
import { PopupModalItemType } from "../../base/modal/PopupModal";
import { networkPortAddModalContent, networkPortUpdateModalContent, floatingIpAddModalContent, securityGroupAddModalContent, snapshotAddModalContent, vmUpdateModalContent, volumeAddModalContent, VmData } from "./VmData";
import { actionMenus, tableHeaders } from "./VmVolumeData";
import { backupAddModalContent } from "../backup/BackupData";


export interface AttachedNetworkPortFloatingIp {
    floatingIpId: string;
    floatingIpAddress: string;
    floatingIpStatusCode: string;
};

export interface AttachedNetworkPortNetwork {
    network_id: string;
    network_name: string;
    external_yn: boolean;
};

export interface AttachedNetworkPortSubnet {
    subnetId: string;
    subnetName: string;
    cidr: string;
    gatewayIpAddress: string;
}

export interface AttachedNetworkPort {
    network_port_id: string;
    network_port_name: string;
    bind_type_code: string;
    up_yn: boolean;
    security_yn: boolean;
    mac_address: string;
    fixed_ip_address: string;
    floating_ip: AttachedNetworkPortFloatingIp;
    network: AttachedNetworkPortNetwork;
    subnet: AttachedNetworkPortSubnet;
};

export interface AttachedVolume {
    volumeId: string;
    volumeName: string;
    volumeDesc: string;
    volumeSizeGb: number;
    volumeStatusCode: string;
    bootYn: boolean;
    device: string;
};


export interface AttachedSecurityGroupRule {
    securityGroupRuleId: string;
    securityGroupRuleTypeCode: string;
    securityGroupDirectionTypeCode: string;
    openPortTypeCode: string;
    minPort: number;
    maxPort: number;
    remoteTypeCode: string;
    remoteCidr: string;
    remoteSecurityGroupName: string;
}

export interface AttachedSecurityGroup {
    securityGroupId: string;
    securityGroupName: string;
    securityGroupRules: AttachedSecurityGroupRule[];
};


export default function VmDetailContainter(props:DetailProps) {
    const {t} = useTranslation();
    const [infoText, setInfoText] = useState('');
    const [infoError, setInfoError] = useState(false);

    const [targetId, setTargetId] = useState(props.targetId || '');

    const [vmName, setVmName] = useState("");
    const [vmDesc, setVmDesc] = useState("");

    const [networkPorts, setNetworkPorts] = useState<AttachedNetworkPort[]>([]);
    const [volumes, setVolumes] = useState<AttachedVolume[]>([]);
    const [securityGroups, setSecurityGroups] = useState<AttachedSecurityGroup[]>([]);
    const [floatingIpNetworkPortId, setFloatingIpNetworkPortId] = useState("");

    const [projectId, setProjectId] = useState("");
    const [domainId, setDomainId] = useState("");

    const [refreshYn, setRefreshYn] = useState(false);
    const [loadVmYn, setLoadVmYn] = useState(false);

    const [executeDeleteNetworkPortId, setExecuteDeleteNetworkPortId] = useState("");
    const [executeDeleteSecurityGroupId, setExecuteDeleteSecurityGroupId] = useState("");
    const [executeDeleteFloatingIpYn, setExecuteDeleteFloatingIpYn] = useState(false);
    const [executeDeleteVolumeYn, setExecuteDeleteVolumeYn] = useState(false);



    const fetchIdRef = useRef(0);
    const [pageCount, setPageCount] = useState(0);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [selectItems, setSelectItems] = useState([]);
    const [detailData, setDetailData] = useState<VmData>();
    const [actionResultTitle, setActionResultTitle] = useState("");
    const [actionResultMessage, setActionResultMessage] = useState("");
    const [actionResults, setActionResults] = useState<PopupModalItemType[]>([]);
    const [actionResultCount, setActionResultCount] = useState(0);

    const headers = useMemo(() => tableHeaders, []);
    const navigate = useNavigate();

    useEffect(() => {
        props.setLoading(false);
        AlertFunction('');
    }, []);

    function AlertFunction(text:string, error=false) {
        setInfoText(text);
        setInfoError(error);
    }

    useEffect(() => {
        if(!targetId) {
            const localTargetId = sessionStorage.getItem(SESSIONSTORAGE.VM_TARGET_ID);
            if(localTargetId) {
                setTargetId(localTargetId);
            } else {
                navigate(`${PAGE_URL.ERRORPAGE}`);
            }
        } else {
            sessionStorage.setItem(SESSIONSTORAGE.VM_TARGET_ID, targetId);
        }
    }, [props]);

    useEffect(() => {
        if(targetId) {
            executeLoadVm();
        }
    }, [targetId]);

    useEffect(() => {
        if(targetId && refreshYn) {
            executeLoadVm();
        }
    }, [refreshYn]);

    useEffect(() => {
        if(executeDeleteNetworkPortId) {
            executeDeleteNetworkPort();
        } else if(executeDeleteSecurityGroupId) {
            executeDeleteSecurityGroup();
        } else if(executeDeleteFloatingIpYn) {
            executeDeleteFloatingIp();
        }

    }, [executeDeleteNetworkPortId, executeDeleteSecurityGroupId, executeDeleteFloatingIpYn]);


    useEffect(() => {
        if(actionResults.length === 0) {
            return ;
        }
        if(actionResultMessage && actionResultTitle) {
            const items = actionResults.map((item, index) => {
                return { target: item.target, successYn: item.successYn, reason: item.reason}
            });
            const completeYn = items.length === selectItems.length ? true : false;
            props.showPopupModalCb({
                openYn: true,
                type: POPUP_TYPE.LIST_MESSAGE_COMPLETE_POPUP,
                title: actionResultTitle,
                message: actionResultMessage,
                items: items,
                completeTargetText: t("VM_VOLUME_NAME_TEXT"),
                code: "COMPLETE",
                cancelButtonYn: false,
                confirmButtonCb: completeAction,
                completeYn: completeYn
            });

            if(completeYn) {
                props.setLoading(false);
            }
        }
        if(actionResults.length > 0) {
            setActionResultCount(actionResultCount - 1);
        }
    }, [actionResults, actionResultTitle, actionResultMessage]);



    useEffect(() => {
        if(actionResultCount === selectItems.length) {
            props.setLoading(true);
        }
        if(actionResultCount > 0) {
            if(executeDeleteVolumeYn) {
                setActionResultTitle(t("VM_VOLUME_DELETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_VOLUME_DELETE_SUCCESS_MESSAGE"));
                executeDeleteVolume(selectItems[selectItems.length - actionResultCount]);
            }
        }
    }, [actionResultCount, executeDeleteVolumeYn]);


    function completeAction(code) {
        setActionResultCount(0);
        setActionResults([]);
        setActionResultMessage("");
        setActionResultTitle("");
        setExecuteDeleteVolumeYn(false);
        setRefreshYn(true);
    }

    function executeResult(target, successYn, reason:string="") {
        const results = actionResults.concat([{target: target, successYn: successYn, reason:reason }]);
        setActionResults(results);
    }

    function executeDeleteNetworkPort() {
        props.setLoading(true);
        requests.delete(PLATFORM_URL.NETWORK_PORT + "/" + executeDeleteNetworkPortId)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeleteNetworkPortId("");
            deleteNetworkPortResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeleteNetworkPortId("");
            deleteNetworkPortResult({successYn: false});
        })
    }

    function executeDeleteSecurityGroup() {
        const deletePackageData = {
            security_group: {
                security_group_id: executeDeleteSecurityGroupId
            },
            vm: {
                vm_id: targetId
            }
        }
        props.setLoading(true);
        requests.post(PLATFORM_URL.SECURITY_GROUP + "/" + executeDeleteSecurityGroupId + "/detach/vm", deletePackageData)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeleteSecurityGroupId("");
            deleteSecurityGroupResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeleteSecurityGroupId("");
            deleteSecurityGroupResult({successYn: false});
        })
    }
    function executeDeleteFloatingIp() {
        const floating_ip_datas = detailData?.network_ports && detailData?.network_ports.map((port)=>{
            if(port.floating_ip.floating_ip_id) {
                return {
                    floating_ip: {
                        floating_ip_id: port.floating_ip.floating_ip_id
                    },
                    network_port_id: port.network_port_id
                };
            }
        });
        const floating_ip_data = floating_ip_datas && floating_ip_datas.length > 0 ? floating_ip_datas[0]: "";
        
        if(!floating_ip_data) {
            setExecuteDeleteFloatingIpYn(false);
            deleteFloatingIpResult({successYn: false});
            return
        }
        const deletePackageData = {
            floating_ip: floating_ip_data.floating_ip
        }
        props.setLoading(true);
        requests.post(PLATFORM_URL.NETWORK_PORT + "/" + floating_ip_data.network_port_id + "/detach/floatingip", deletePackageData)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeleteFloatingIpYn(false);
            deleteSecurityGroupResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeleteFloatingIpYn(false);
            deleteSecurityGroupResult({successYn: false});
        })
    }
    function executeDeleteVolume(item) {
        const deletePackageData = {
            volume: {
                volume_id: item.volume_id
            },
            vm: {
                vm_id: targetId
            }
        }
        requests.post(PLATFORM_URL.VOLUME + "/" + item.volume_id + "/detach/vm", deletePackageData)
        .then((response) => {
            executeResult(item.volume_name, true);
        })
        .catch((error) => {
            executeResult(item.volume_name, false);
        })
    }


    function executeLoadVm() {
        props.setLoading(true);
        requests.get(PLATFORM_URL.VM + "/" + targetId)
        .then((response) => {
            setLoadVmYn(true);
            if (response.status === 200 && response.data) {
                setDetailData(response.data);
            }
            setRefreshYn(false);
            props.setLoading(false);
        })
        .catch((error) => {
            //show error message
            setLoadVmYn(true);
            AlertFunction(t("MESSAGE_CANNOT_LOAD_DATA_MESSAGE"), true);
            setRefreshYn(false);
            props.setLoading(false);
        });
    }

    function addSnapshotResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_SNAPSHOT_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function updateVmResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }


    function addNetworkPortResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_NETWORK_PORT_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function updateNetworkPortResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_NETWORK_PORT_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function deleteNetworkPortResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_NETWORK_PORT_DELETE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        } else {
            AlertFunction(t("MESSAGE_VM_NETWORK_PORT_DELETE_FAIL_MESSAGE"));
        }
    }

    function addFloatingIpResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_FLOATING_IP_ADD_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function deleteFloatingIpResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_FLOATING_IP_DELETE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        } else {
            AlertFunction(t("MESSAGE_VM_FLOATING_IP_DELETE_FAIL_MESSAGE"));
        }
    }

    function addSecurityGroupResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_SECURITY_GROUP_ADD_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function deleteSecurityGroupResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_SECURITY_GROUP_DELETE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        } else {
            AlertFunction(t("MESSAGE_VM_SECURITY_GROUP_DELETE_FAIL_MESSAGE"));
        }
    }

    function addVolumeResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_VOLUME_ADD_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }


    function clickVmUpdate() {
        const data = {
            vmId: targetId,
            vmName: detailData?.vm_name,
            vmDesc: detailData?.vm_description,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...vmUpdateModalContent, openYn: true, resultCb: updateVmResult, args: data});
        }
    }

    function clickSnapshotAdd() {
        const data = {
            vmId: targetId,
            vmName: detailData?.vm_name,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...snapshotAddModalContent, openYn: true, resultCb: addSnapshotResult, args: data});
        }
    }

    function clickNetworkPortUpdate(item) {
        const data = {
            vmId: targetId,
            networkPortId: item.network_port_id,
            networkPortName: item.network_port_name,
            upYn: item.up_yn
        }

        if(props.showSlideModalCb) {
            props.showSlideModalCb({...networkPortUpdateModalContent, openYn: true, resultCb: updateNetworkPortResult, args: data});
        }
    }


    function clickNetworkPortDelete(item) {
        props.showPopupModalCb({openYn: true,
                    type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
                    title: t("MESSAGE_VM_NETWORK_PORT_DELETE_REQUEST_TITLE_MESSAGE"),
                    message: t("MESSAGE_VM_NETWORK_PORT_DELETE_REQUEST_MESSAGE"),
                    code: "",
                    cancelButtonYn: true,
                    confirmButtonCb: (code) => {setExecuteDeleteNetworkPortId(item.network_port_id)},
        });
    }

    function clickNetworkPortAdd() {
        const data = {
            vmId: targetId,
            projectId: detailData?.project?.project_id
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...networkPortAddModalContent, openYn: true, resultCb: addNetworkPortResult, args: data});
        }
    }

    function clickFloatingIpDelete(item) {
        props.showPopupModalCb({openYn: true,
            type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
            title: t("MESSAGE_VM_FLOATING_IP_DELETE_REQUEST_TITLE_MESSAGE"),
            message: t("MESSAGE_VM_FLOATING_IP_DELETE_REQUEST_MESSAGE"),
            code: "",
            cancelButtonYn: true,
            confirmButtonCb: (code) => {setExecuteDeleteFloatingIpYn(true)},
        });
    }

    function clickFloatingIpAdd() {
        const data = {
            vmId: detailData?.vm_id,
            projectId: detailData?.project.project_id,
            networkPorts: detailData?.network_ports
        };
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...floatingIpAddModalContent, openYn: true, resultCb: addFloatingIpResult, args: data});
        }
    }



    function clickSecurityGroupDelete(item) {
        props.showPopupModalCb({openYn: true,
                    type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
                    title: t("MESSAGE_VM_SECURITY_GROUP_DELETE_REQUEST_TITLE_MESSAGE"),
                    message: t("MESSAGE_VM_SECURITY_GROUP_DELETE_REQUEST_MESSAGE"),
                    code: "",
                    cancelButtonYn: true,
                    confirmButtonCb: (code) => {setExecuteDeleteSecurityGroupId(item.security_group_id)},
        });
    }

    function clickSecurityGroupAdd() {
        const data = {
            vmId: targetId,
            securityGroups: detailData?.security_groups,
            projectId: detailData?.project.project_id,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...securityGroupAddModalContent, openYn: true, resultCb: addSecurityGroupResult, args: data});
        }
    }

    function clickVolumeDelete() {
        props.showPopupModalCb({openYn: true,
                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                    title: t("MESSAGE_VM_VOLUME_DELETE_REQUEST_TITLE_MESSAGE"),
                    message: t("MESSAGE_VM_VOLUME_DELETE_REQUEST_MESSAGE"),
                    items: selectItems.map((item: any, index) => {
                            return { target: item.volume_name }
                    }),
                    code: "",
                    cancelButtonYn: true,
                    confirmButtonCb: (code) => {setActionResultCount(selectItems.length); setExecuteDeleteVolumeYn(true);},
        });
    }
    function clickVolumeAdd() {
        const data = {
            vmId: targetId,
            projectId: detailData?.project?.project_id,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...volumeAddModalContent, openYn: true, resultCb: addVolumeResult, args: data});
        }
    }

    function clickBackupAdd() {
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...backupAddModalContent, openYn: true, resultCb: addVolumeResult, args: {vmId: targetId, projectId: detailData?.project?.project_id, vmName: detailData?.vm_name, projectName: detailData?.project?.project_name}});
        }
    }


    function selectRow(items) {
        // !!선택 테이블인 경우 액션 메뉴 상태 변경 커스텀
        setSelectItems(items);

        if(items.length > 0) {
            setActionMenuStatus(2, true);

        } else {
            setActionMenuStatus(2, false);
        }
    }
    function setActionMenuStatus(id, activeYn) {
        actionMenus[id - 1].activeYn = activeYn;
    }
    const fetchData = useCallback(({ pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search }) => {

    }, []);


    function clickConnectVdi() {
        const connectData = {
            vm: {
                vm_id: targetId
            }
        }
        props.setLoading(true);
        requests.post(PLATFORM_URL.VDI_SESSION + "/gen", connectData)
        .then((response) => {
            setTimeout(() => {props.setLoading(false);}, 500);
            var api_gw = "https://" + response.data.apigw_url
            const fornaxurl = "FornaxViewerManager://?" + base64Encoding("usapi-url=" + api_gw + '&usapi-tok=' + response.data.access_token.replace('=', '%3F') + '&vm-id=' + response.data.vm.vm_id + '&vm-name=' + response.data.vm.vm_name + '&ss-type=service');
            window.open(fornaxurl, "_self");
            
        })
        .catch((error) => {
            props.setLoading(false);
        })
    }

    return (
            targetId ?
            <VmDetail
                floatingIpNetworkPortId={floatingIpNetworkPortId}
                infoText={infoText}
                infoError={infoError}
                connectVdiFunction={clickConnectVdi}
                updateVmFunction={clickVmUpdate}
                addFloatingIpFunction={clickFloatingIpAdd}
                deleteFloatingIpFunction={clickFloatingIpDelete}
                updateNetworkPortFunction={clickNetworkPortUpdate}
                addSnapshotFunction={clickSnapshotAdd}
                addVmBackupFunction={clickBackupAdd}
                addNetworkPortFunction={clickNetworkPortAdd}
                addSecurityGroupFunction={clickSecurityGroupAdd}
                addVolumeFunction={clickVolumeAdd}
                deleteNetworkPortFunction={clickNetworkPortDelete}
                deleteSecurityGroupFunction={clickSecurityGroupDelete}
                deleteVolumeFunction={clickVolumeDelete}
                refreshYn={refreshYn}
                loadMainDataYn={loadVmYn}
                selectVolumeRow={selectRow}
                fetchVolumeData={fetchData}
                totalVolumeCount={totalItemCount}
                volumeHeaders={headers}
                detailData={detailData ? detailData : null}
            />
            : null
    );
}
